import { useState } from 'react'
import { ScreeningViewTemplate } from '../ScreeningTemplate'
import {
  useSelectUserBirthYear,
  useSelectFormattedScreeningHandledDate,
  useSelectFirstName,
} from '../../../../state/selectors'
import { TextInput } from '../../../inputs/Text'
import { theme } from '../../../../theme/theme'
import { formatPersonalNumber } from '../../../../utils/personalNumber'
import { useScreeningDescriptionText, useStartScreening } from '../ScreeningShared'
import {
  JournalConsentCheckbox,
  OptionalJournalSection,
  useScreeningSummaryOptIn,
} from '../../../consent/JournalConsent'
import { useScreeningConsent, ScreeningConsentCheckbox } from '../../ScreeningConsent'
import { ScreeningType, isValidPersonalNumberFormat, TrackingEvents } from '@estercare/ester-shared'
import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useEmitEvent } from '../../../../hooks/analytics/useEmitEvent'
import { ConsentId } from '../../../../api/ester-b2b-api/private/consent/types'
import { queryErrorHandlerWithMessage } from '../../../../utils/queryErrorHandler'
import { useGetUserHealthPlanOutcomesQuery } from '../../../../api/ester-b2b-api/private/healthPlan/healthPlanQueries'
import { Outcomes } from '../../../healthPlan/Outcomes'

export const ScreeningCheckInNotStarted = () => {
  const { t } = useTranslation()
  const emitEvent = useEmitEvent()

  const { consentData, isConsentLoading, consentCheckbox, setConsentCheckbox, setUserConsent, isButtonDisabled } =
    useScreeningConsent()
  const summaryOptIn = useScreeningSummaryOptIn()
  const birthYear = useSelectUserBirthYear()
  const [error, setError] = useState<string | null>(null)
  const [personalNumber, setPersonalNumber] = useState('')

  const { startScreening, isStartPending } = useStartScreening()
  const parentScreeningDate = useSelectFormattedScreeningHandledDate()
  const firstName = useSelectFirstName()
  const { data: outcomesData } = useGetUserHealthPlanOutcomesQuery()
  const isPersonalNumberValid = isValidPersonalNumberFormat(personalNumber)
  const screeningDescription = useScreeningDescriptionText(ScreeningType.CHECK_IN, parentScreeningDate || '')
  const onPersonalNumberChange = (value: string) => {
    setPersonalNumber(formatPersonalNumber(value))
  }

  const { askForPersonalNumber } = summaryOptIn
  const isPersonalNumberMissing = askForPersonalNumber && !isPersonalNumberValid

  const onButtonClick = async () => {
    emitEvent({
      eventType: TrackingEvents.CLICK,
      action: 'check_in_start',
    })

    const saveOptInAndStart = async () => {
      try {
        await summaryOptIn.saveOptIn()
      } catch (error) {
        setError(queryErrorHandlerWithMessage('save screening summary consent', t)(error as Error))
        return
      }
      await startScreening(ScreeningType.CHECK_IN, setError, askForPersonalNumber ? personalNumber : undefined)
    }

    if (!consentData) {
      setUserConsent(ConsentId.SCREENING_CONSENT, {
        onSuccess: saveOptInAndStart,
        onError: (error) => {
          const message = queryErrorHandlerWithMessage('set user screening consent and start check-in', t)(error)
          setError(message)
        },
      })
    } else {
      await saveOptInAndStart()
    }
  }
  return (
    <ScreeningViewTemplate
      pageTitle={t('employee.screening.checkInNotStartedPage')}
      screeningTitle={t('employee.screening.checkInTitle', { firstName })}
      screeningDescription={screeningDescription}
      leftAlignText
      screeningAdditionalChildren={
        askForPersonalNumber || summaryOptIn.showOptIn || (!isConsentLoading && !consentData) ? (
          <Stack textAlign="left" gap={`${theme.spacing.mediumLarge}px`}>
            {!isConsentLoading && !consentData && (
              <ScreeningConsentCheckbox consentCheckbox={consentCheckbox} setConsentCheckbox={setConsentCheckbox} />
            )}
            {(summaryOptIn.showOptIn || askForPersonalNumber) && (
              <OptionalJournalSection>
                {summaryOptIn.showOptIn && (
                  <JournalConsentCheckbox
                    title={t('employee.screening.journalTitle')}
                    text={t('employee.screening.journalText')}
                    label={t('employee.screening.journalCheckbox')}
                    checked={summaryOptIn.checked}
                    onChange={summaryOptIn.setChecked}
                  />
                )}
                {askForPersonalNumber && (
                  <TextInput
                    label={t('common.form.personalNumber')}
                    onChange={(e) => onPersonalNumberChange(e.target.value)}
                    value={personalNumber || ''}
                    placeholder={birthYear ? `${birthYear}XXXX-XXXX` : '19XXXXXX-XXXX'}
                    borderColor={isPersonalNumberValid ? theme.color.green : undefined}
                  />
                )}
              </OptionalJournalSection>
            )}
          </Stack>
        ) : undefined
      }
      fullWidthChildren={
        outcomesData?.outcomes?.length ? (
          <Outcomes
            outcomes={outcomesData.outcomes}
            title={t('employee.screening.outcomesTitle', { date: parentScreeningDate })}
            description={t('employee.screening.outcomesDescription')}
          />
        ) : undefined
      }
      buttonText={t('employee.screening.checkInNotStartedButton')}
      onButtonClick={onButtonClick}
      isButtonDisabled={isButtonDisabled || isPersonalNumberMissing}
      isLoading={isStartPending}
      errorMsg={error || undefined}
    />
  )
}
